import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';

import Modal from '../../stories/ui/Modal/Modal';
import Button from '../../stories/ui/Button/Button';
import TextField from '../../stories/ui/TextField/TextField';
import Alert from '../../alerts/Alert';
import { authRequest } from '../../api/requestWrappers/AuthRequest';
import { authUserSelector } from '../../store/auth/selectors';
import { buildPasswordSchema } from '../../utils/passwordValidation';
import useTextFieldErrorClasses from '../../hooks/styles/useTextFieldErrorClasses';
import InputErrorIcon from '../../components/ui/InputErrorIcon';

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0;
  min-width: 360px;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
`;

export default function ChangePasswordModal({ open, onClose }) {
  const { t } = useTranslation();
  const authUser = useSelector(authUserSelector);
  const textFieldErrorClasses = useTextFieldErrorClasses();
  const [loading, setLoading] = useState(false);

  const Schema = Yup.object().shape({
    currentPassword: Yup.string().required(t('passwordRequired')),
    newPassword: buildPasswordSchema(t).test(
      'differs-from-current',
      t('changePassword.sameAsCurrent'),
      function differsFromCurrent(value) {
        // eslint-disable-next-line react/no-this-in-sfc
        return !value || value !== this.parent.currentPassword;
      }
    ),
    newPasswordConfirm: Yup.string()
      .oneOf([Yup.ref('newPassword')], t('changePassword.confirmMismatch'))
      .required(t('passwordRequired')),
  });

  const renderPasswordField = (name, placeholder) => (
    <Field name={name}>
      {({ field, meta }) => (
        <StyledTextField
          type="password"
          id={`change-password-${name}`}
          placeholder={placeholder}
          {...field}
          InputProps={
            meta.touched && meta.error
              ? {
                  endAdornment: (
                    <InputErrorIcon title={meta.error} permanentOpen />
                  ),
                  classes: textFieldErrorClasses,
                }
              : undefined
          }
        />
      )}
    </Field>
  );

  return (
    <Formik
      initialValues={{
        currentPassword: '',
        newPassword: '',
        newPasswordConfirm: '',
      }}
      validationSchema={Schema}
      onSubmit={async (values, { setFieldError, resetForm }) => {
        setLoading(true);
        try {
          await authRequest.changePassword(
            authUser.email,
            values.currentPassword,
            values.newPassword
          );
          Alert.showSuccess('changePassword.successToast');
          resetForm();
          onClose();
        } catch (e) {
          const status = e?.response?.status;
          const code = e?.response?.data?.code;
          if (code === 'USER_NEW_PASSWORD_SAME_AS_OLD') {
            setFieldError('newPassword', t('changePassword.sameAsCurrent'));
          } else if (status === 401 || status === 403) {
            setFieldError(
              'currentPassword',
              t('changePassword.incorrectCurrent')
            );
          } else {
            Alert.showError('changePassword.errorGeneric');
          }
        } finally {
          setLoading(false);
        }
      }}
    >
      {({ submitForm, resetForm }) => {
        const handleClose = () => {
          resetForm();
          onClose();
        };
        return (
          <Modal
            openModal={open}
            onModalClose={handleClose}
            title={t('changePassword.modalTitle')}
            showTitle
            showCloseIcon
            actions={({ closeRouteModal }) => (
              <>
                <Button
                  type="button"
                  onClick={() => closeRouteModal()}
                  color="danger"
                  isLoading={loading}
                >
                  <Trans i18nKey="changePassword.cancel" />
                </Button>
                <Button
                  type="button"
                  onClick={() => submitForm()}
                  color="primary"
                  size="large"
                  isLoading={loading}
                >
                  <Trans i18nKey="changePassword.submit" />
                </Button>
              </>
            )}
          >
            <Form>
              <FieldsWrapper>
                {renderPasswordField(
                  'currentPassword',
                  t('changePassword.currentLabel')
                )}
                {renderPasswordField(
                  'newPassword',
                  t('changePassword.newLabel')
                )}
                {renderPasswordField(
                  'newPasswordConfirm',
                  t('changePassword.confirmLabel')
                )}
              </FieldsWrapper>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
}

ChangePasswordModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
